(() => {
  if (window.BlinkBoxBlogCarousel) {
    window.BlinkBoxBlogCarousel.init(document);
    return;
  }

  let blogCarouselCount = 0;

  class BlogCarousel {
    constructor(carousel) {
      this.carousel = carousel;
      this.connect();
    }

    connect() {
      if (this.carousel.dataset.blogCarouselReady === 'true') return;

      this.slides = Array.from(
        this.carousel.querySelectorAll(':scope > .blog-slide')
      );

      if (!this.slides.length) return;

      blogCarouselCount += 1;
      this.carousel.dataset.blogCarouselReady = 'true';
      this.carousel.setAttribute('role', 'region');
      this.carousel.setAttribute('aria-roledescription', 'carousel');

      const carouselLabel =
        this.carousel.getAttribute('aria-label') || 'Image carousel';
      const viewportId = `blog-carousel-${blogCarouselCount}`;

      this.viewport = document.createElement('div');
      this.viewport.className = 'blog-carousel__viewport';
      this.viewport.id = viewportId;
      this.viewport.tabIndex = 0;
      this.viewport.setAttribute('role', 'list');
      this.viewport.setAttribute('aria-label', carouselLabel);

      this.slides.forEach((slide, index) => {
        slide.setAttribute('role', 'listitem');

        const title = slide.querySelector(
          ':scope > strong, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6'
        );

        if (title) {
          if (!title.id) {
            title.id = `${viewportId}-slide-${index + 1}-title`;
          }

          slide.setAttribute('aria-labelledby', title.id);
        }

        this.viewport.appendChild(slide);
      });

      this.previousButton = this.createButton(
        'previous',
        'Previous slides',
        '‹'
      );

      this.nextButton = this.createButton(
        'next',
        'Next slides',
        '›'
      );

      this.previousButton.setAttribute('aria-controls', viewportId);
      this.nextButton.setAttribute('aria-controls', viewportId);

      this.progress = document.createElement('div');
      this.progress.className = 'blog-carousel__progress';
      this.progress.setAttribute('aria-hidden', 'true');

      this.progressFill = document.createElement('span');
      this.progressFill.className = 'blog-carousel__progress-fill';
      this.progress.appendChild(this.progressFill);

      this.controls = document.createElement('div');
      this.controls.className = 'blog-carousel__controls';
      this.controls.append(
        this.previousButton,
        this.progress,
        this.nextButton
      );

      this.carousel.append(this.viewport, this.controls);

      this.previousButton.addEventListener('click', () => {
        this.scrollByPage(-1);
      });

      this.nextButton.addEventListener('click', () => {
        this.scrollByPage(1);
      });

      this.viewport.addEventListener('keydown', (event) => {
        this.handleKeydown(event);
      });

      this.viewport.addEventListener(
        'scroll',
        () => {
          window.requestAnimationFrame(() => this.update());
        },
        { passive: true }
      );

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', () => this.update());
      }

      window.requestAnimationFrame(() => this.update());
    }

    createButton(direction, label, symbol) {
      const button = document.createElement('button');
      button.className =
        `blog-carousel__button blog-carousel__button--${direction}`;
      button.type = 'button';
      button.setAttribute('aria-label', label);

      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = symbol;
      button.appendChild(icon);

      return button;
    }

    getMaxScroll() {
      return Math.max(
        this.viewport.scrollWidth - this.viewport.clientWidth,
        0
      );
    }

    getPageDistance() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return 0;

      const gap =
        parseFloat(
          window.getComputedStyle(this.viewport).columnGap || '0'
        ) || 0;

      const slideStep =
        firstSlide.getBoundingClientRect().width + gap;

      const visibleSlides = Math.max(
        Math.floor(
          (this.viewport.clientWidth + gap) / slideStep
        ),
        1
      );

      return visibleSlides * slideStep;
    }

    scrollByPage(direction) {
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.viewport.scrollBy({
        left: direction * this.getPageDistance(),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollByPage(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollByPage(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.viewport.scrollTo({
          left: 0,
          behavior: 'auto'
        });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.viewport.scrollTo({
          left: this.getMaxScroll(),
          behavior: 'auto'
        });
      }
    }

    update() {
      const maxScroll = this.getMaxScroll();
      const currentScroll = Math.min(
        Math.max(this.viewport.scrollLeft, 0),
        maxScroll
      );

      const hasOverflow = maxScroll > 2;

      this.carousel.dataset.blogCarouselOverflow =
        String(hasOverflow);

      this.previousButton.disabled =
        !hasOverflow || currentScroll <= 2;

      this.nextButton.disabled =
        !hasOverflow || currentScroll >= maxScroll - 2;

      const width = hasOverflow
        ? Math.min(
            Math.max(
              (this.viewport.clientWidth /
                this.viewport.scrollWidth) *
                100,
              10
            ),
            100
          )
        : 100;

      const left =
        hasOverflow && maxScroll
          ? (currentScroll / maxScroll) * (100 - width)
          : 0;

      this.progressFill.style.setProperty(
        '--blog-carousel-progress-width',
        `${width}%`
      );

      this.progressFill.style.setProperty(
        '--blog-carousel-progress-left',
        `${left}%`
      );
    }
  }

  function init(root = document) {
    const carousels = [];

    if (
      root instanceof Element &&
      root.matches('.article-content .blog-carousel')
    ) {
      carousels.push(root);
    }

    if (root.querySelectorAll) {
      carousels.push(
        ...root.querySelectorAll(
          '.article-content .blog-carousel'
        )
      );
    }

    carousels.forEach((carousel) => {
      if (
        carousel.dataset.blogCarouselReady !== 'true'
      ) {
        new BlogCarousel(carousel);
      }
    });
  }

  window.BlinkBoxBlogCarousel = { init };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      { once: true }
    );
  } else {
    init(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target)
  );
})();